import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, EditIcon, TrashIcon, BuildingIcon, CheckIcon, XIcon, ClockIcon, DollarSignIcon, ListTodoIcon, PencilIcon, PackageIcon, PlusIcon, FileTextIcon, ChevronRightIcon, AlertCircleIcon, UploadIcon, FileIcon, DownloadIcon, UserIcon, PaperclipIcon } from 'lucide-react';
import Button from './common/Button';
import { mockProjects, mockProperties } from '../data/mockData';
// Mock data for project schedule
const mockSchedule = [{
  id: 'phase1',
  name: 'Pre-Construction',
  startDate: '2023-03-01',
  endDate: '2023-03-20',
  status: 'completed',
  tasks: [{
    name: 'Design Finalization',
    startDate: '2023-03-01',
    endDate: '2023-03-10',
    status: 'completed'
  }, {
    name: 'Permits & Approvals',
    startDate: '2023-03-10',
    endDate: '2023-03-20',
    status: 'completed'
  }]
}, {
  id: 'phase2',
  name: 'Demolition',
  startDate: '2023-03-21',
  endDate: '2023-04-05',
  status: 'completed',
  tasks: [{
    name: 'Interior Demolition',
    startDate: '2023-03-21',
    endDate: '2023-03-30',
    status: 'completed'
  }, {
    name: 'Debris Removal',
    startDate: '2023-03-31',
    endDate: '2023-04-05',
    status: 'completed'
  }]
}, {
  id: 'phase3',
  name: 'Rough-In Work',
  startDate: '2023-04-06',
  endDate: '2023-05-15',
  status: 'in-progress',
  tasks: [{
    name: 'Plumbing Rough-In',
    startDate: '2023-04-06',
    endDate: '2023-04-20',
    status: 'completed'
  }, {
    name: 'Electrical Rough-In',
    startDate: '2023-04-21',
    endDate: '2023-05-05',
    status: 'completed'
  }, {
    name: 'HVAC Installation',
    startDate: '2023-05-06',
    endDate: '2023-05-15',
    status: 'in-progress'
  }]
}, {
  id: 'phase4',
  name: 'Finishes',
  startDate: '2023-05-16',
  endDate: '2023-07-15',
  status: 'not-started',
  tasks: [{
    name: 'Drywall Installation',
    startDate: '2023-05-16',
    endDate: '2023-05-30',
    status: 'not-started'
  }, {
    name: 'Painting',
    startDate: '2023-06-01',
    endDate: '2023-06-15',
    status: 'not-started'
  }, {
    name: 'Flooring Installation',
    startDate: '2023-06-16',
    endDate: '2023-06-30',
    status: 'not-started'
  }, {
    name: 'Kitchen & Bath Installation',
    startDate: '2023-07-01',
    endDate: '2023-07-15',
    status: 'not-started'
  }]
}, {
  id: 'phase5',
  name: 'Final Inspection & Closeout',
  startDate: '2023-07-16',
  endDate: '2023-07-30',
  status: 'not-started',
  tasks: [{
    name: 'Final Cleaning',
    startDate: '2023-07-16',
    endDate: '2023-07-20',
    status: 'not-started'
  }, {
    name: 'Punch List Items',
    startDate: '2023-07-21',
    endDate: '2023-07-25',
    status: 'not-started'
  }, {
    name: 'Final Inspection',
    startDate: '2023-07-26',
    endDate: '2023-07-30',
    status: 'not-started'
  }]
}];
// Mock data for budget and change orders
const mockBudget = {
  categories: [{
    name: 'Demolition',
    original: 15000,
    current: 16200,
    actual: 16200,
    variance: -1200
  }, {
    name: 'Framing & Carpentry',
    original: 45000,
    current: 45000,
    actual: 20000,
    variance: 25000
  }, {
    name: 'Plumbing',
    original: 35000,
    current: 38500,
    actual: 38500,
    variance: -3500
  }, {
    name: 'Electrical',
    original: 40000,
    current: 40000,
    actual: 38000,
    variance: 2000
  }, {
    name: 'HVAC',
    original: 30000,
    current: 32000,
    actual: 15000,
    variance: 17000
  }, {
    name: 'Drywall & Painting',
    original: 28000,
    current: 28000,
    actual: 0,
    variance: 28000
  }, {
    name: 'Flooring',
    original: 42000,
    current: 45000,
    actual: 0,
    variance: 45000
  }, {
    name: 'Kitchen & Bath',
    original: 65000,
    current: 68000,
    actual: 0,
    variance: 68000
  }, {
    name: 'Fixtures & Appliances',
    original: 35000,
    current: 35000,
    actual: 0,
    variance: 35000
  }, {
    name: 'Miscellaneous',
    original: 15000,
    current: 12300,
    actual: 12300,
    variance: 0
  }],
  summary: {
    originalTotal: 350000,
    currentTotal: 360000,
    actualToDate: 140000,
    remainingBudget: 220000,
    overallVariance: -10000
  }
};
const mockChangeOrders = [{
  id: 'co1',
  number: 'CO-001',
  description: 'Additional demolition work required due to discovered water damage',
  date: '2023-03-25',
  amount: 1200,
  status: 'approved'
}, {
  id: 'co2',
  number: 'CO-002',
  description: 'Upgrade plumbing fixtures in all bathrooms',
  date: '2023-04-10',
  amount: 3500,
  status: 'approved'
}, {
  id: 'co3',
  number: 'CO-003',
  description: 'Add recessed lighting in living room',
  date: '2023-05-05',
  amount: 2000,
  status: 'pending'
}, {
  id: 'co4',
  number: 'CO-004',
  description: 'Upgrade HVAC system to higher efficiency unit',
  date: '2023-05-10',
  amount: 2000,
  status: 'approved'
}, {
  id: 'co5',
  number: 'CO-005',
  description: 'Premium flooring upgrade in master bedroom',
  date: '2023-06-01',
  amount: 3000,
  status: 'pending'
}];
// Mock data for material selections
const mockMaterials = {
  categories: [{
    name: 'Flooring',
    items: [{
      name: 'Living Room Hardwood',
      description: 'Engineered oak hardwood, 5" planks, natural finish',
      brand: 'Armstrong',
      model: 'Prime Harvest Oak Natural',
      cost: 8.75,
      unit: 'sq ft',
      quantity: 500,
      totalCost: 4375,
      status: 'selected',
      notes: 'Install in herringbone pattern'
    }, {
      name: 'Kitchen Tile',
      description: 'Porcelain tile, 12"x24", slate gray',
      brand: 'Daltile',
      model: 'Panoramic Porcelain Surfaces',
      cost: 6.5,
      unit: 'sq ft',
      quantity: 300,
      totalCost: 1950,
      status: 'selected',
      notes: 'Install in staggered pattern with 1/8" grout lines'
    }, {
      name: 'Bathroom Floor Tile',
      description: 'Hexagon mosaic marble tile, white',
      brand: 'MSI',
      model: 'Greecian White Hexagon',
      cost: 12.25,
      unit: 'sq ft',
      quantity: 120,
      totalCost: 1470,
      status: 'pending',
      notes: 'Need final approval'
    }]
  }, {
    name: 'Cabinetry',
    items: [{
      name: 'Kitchen Cabinets',
      description: 'Shaker style, soft-close drawers, white finish',
      brand: 'KraftMaid',
      model: 'Deveron Maple',
      cost: 18500,
      unit: 'total',
      quantity: 1,
      totalCost: 18500,
      status: 'ordered',
      notes: 'Delivery expected in 6 weeks'
    }, {
      name: 'Bathroom Vanity - Master',
      description: 'Double sink, gray finish, marble top',
      brand: 'Allen + Roth',
      model: 'Kingscote Espresso',
      cost: 1800,
      unit: 'each',
      quantity: 1,
      totalCost: 1800,
      status: 'selected',
      notes: ''
    }]
  }, {
    name: 'Countertops',
    items: [{
      name: 'Kitchen Countertops',
      description: 'Quartz, white with light gray veining',
      brand: 'Silestone',
      model: 'Eternal Calacatta Gold',
      cost: 75,
      unit: 'sq ft',
      quantity: 65,
      totalCost: 4875,
      status: 'selected',
      notes: 'Include 4" backsplash'
    }]
  }, {
    name: 'Fixtures',
    items: [{
      name: 'Kitchen Faucet',
      description: 'Pull-down sprayer, brushed nickel',
      brand: 'Kohler',
      model: 'Sous Pro-Style',
      cost: 329,
      unit: 'each',
      quantity: 1,
      totalCost: 329,
      status: 'ordered',
      notes: ''
    }, {
      name: 'Bathroom Faucets',
      description: 'Widespread, matte black finish',
      brand: 'Delta',
      model: 'Trinsic',
      cost: 189,
      unit: 'each',
      quantity: 3,
      totalCost: 567,
      status: 'selected',
      notes: ''
    }, {
      name: 'Shower Fixtures',
      description: 'Rain shower head with hand shower, brushed gold',
      brand: 'Moen',
      model: 'Attract Magnetix',
      cost: 425,
      unit: 'each',
      quantity: 2,
      totalCost: 850,
      status: 'pending',
      notes: 'Waiting for client approval'
    }]
  }, {
    name: 'Appliances',
    items: [{
      name: 'Refrigerator',
      description: 'French door, counter-depth, stainless steel',
      brand: 'GE Profile',
      model: 'PYE22PYNFS',
      cost: 3299,
      unit: 'each',
      quantity: 1,
      totalCost: 3299,
      status: 'selected',
      notes: ''
    }, {
      name: 'Range',
      description: '30" gas range, 5 burners, convection oven',
      brand: 'KitchenAid',
      model: 'KFGC500JMH',
      cost: 2499,
      unit: 'each',
      quantity: 1,
      totalCost: 2499,
      status: 'selected',
      notes: ''
    }, {
      name: 'Dishwasher',
      description: 'Stainless steel interior, hidden controls',
      brand: 'Bosch',
      model: 'SHPM88Z75N',
      cost: 1299,
      unit: 'each',
      quantity: 1,
      totalCost: 1299,
      status: 'selected',
      notes: ''
    }]
  }]
};
// Mock specifications
const mockSpecifications = [{
  id: 'spec1',
  title: 'Plumbing Specifications',
  content: 'All supply lines to be PEX-A with brass fittings. Drain lines to be ABS. Water heater to be tankless Rinnai model RU199iN with recirculation pump.',
  category: 'Plumbing',
  lastUpdated: '2023-04-05'
}, {
  id: 'spec2',
  title: 'Electrical Specifications',
  content: 'All wiring to be 12-gauge copper with AFCI protection in bedrooms. LED recessed lighting throughout with Lutron Caseta smart switches and dimmers.',
  category: 'Electrical',
  lastUpdated: '2023-04-10'
}, {
  id: 'spec3',
  title: 'Insulation Requirements',
  content: 'R-19 in exterior walls, R-38 in attic spaces. Spray foam insulation around all window and door openings. Acoustic insulation in interior walls of bedrooms and bathrooms.',
  category: 'Insulation',
  lastUpdated: '2023-04-15'
}, {
  id: 'spec4',
  title: 'Flooring Installation Guidelines',
  content: 'Hardwood to be acclimated in the space for minimum 72 hours prior to installation. Subfloor moisture content not to exceed 12%. 1/2" expansion gap at all perimeter walls.',
  category: 'Flooring',
  lastUpdated: '2023-05-01'
}, {
  id: 'spec5',
  title: 'Paint Specifications',
  content: 'All walls to receive one coat of primer and two coats of Sherwin Williams Duration in eggshell finish. Trim to be painted with semi-gloss. Ceiling flat white.',
  category: 'Painting',
  lastUpdated: '2023-05-05'
}];
// Mock contracts data
const mockContracts = [{
  id: 'contract1',
  number: 'CT-001',
  title: 'Demolition Services Agreement',
  vendor: 'ABC Demolition Co.',
  amount: 16200,
  startDate: '2023-03-21',
  completionDate: '2023-04-05',
  status: 'completed',
  dateCreated: '2023-03-15',
  documents: [{
    name: 'Demolition_Contract.pdf',
    type: 'contract',
    size: '1.2 MB'
  }, {
    name: 'Scope_of_Work.pdf',
    type: 'addendum',
    size: '450 KB'
  }],
  terms: 'Payment terms: 50% upon commencement, 50% upon completion. Contractor responsible for all permits and waste disposal.'
}, {
  id: 'contract2',
  number: 'CT-002',
  title: 'Plumbing Installation Contract',
  vendor: 'Superior Plumbing Services',
  amount: 38500,
  startDate: '2023-04-06',
  completionDate: '2023-04-20',
  status: 'completed',
  dateCreated: '2023-04-01',
  documents: [{
    name: 'Plumbing_Agreement.pdf',
    type: 'contract',
    size: '1.5 MB'
  }, {
    name: 'Fixture_Schedule.xlsx',
    type: 'addendum',
    size: '320 KB'
  }],
  terms: 'Payment schedule: 30% deposit, 30% rough-in completion, 40% final. All fixtures to be provided by owner unless specified.'
}, {
  id: 'contract3',
  number: 'CT-003',
  title: 'Electrical Services Agreement',
  vendor: 'Bright Spark Electrical',
  amount: 40000,
  startDate: '2023-04-21',
  completionDate: '2023-05-05',
  status: 'completed',
  dateCreated: '2023-04-15',
  documents: [{
    name: 'Electrical_Contract.pdf',
    type: 'contract',
    size: '1.3 MB'
  }],
  terms: 'Payment terms: 25% deposit, 25% rough-in, 25% trim-out, 25% final. All work to be performed to code and inspected.'
}, {
  id: 'contract4',
  number: 'CT-004',
  title: 'HVAC Installation Agreement',
  vendor: 'Climate Control Systems',
  amount: 32000,
  startDate: '2023-05-06',
  completionDate: '2023-05-15',
  status: 'in-progress',
  dateCreated: '2023-05-01',
  documents: [{
    name: 'HVAC_Contract.pdf',
    type: 'contract',
    size: '1.7 MB'
  }, {
    name: 'Equipment_Specs.pdf',
    type: 'addendum',
    size: '890 KB'
  }],
  terms: 'Payment schedule: 40% deposit, 30% equipment delivery, 30% completion. 1-year labor warranty, manufacturer warranty on all equipment.'
}];
const ProjectDetail = () => {
  const {
    id
  } = useParams();
  const project = mockProjects.find(p => p.id === id);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewChangeOrderForm, setShowNewChangeOrderForm] = useState(false);
  const [newChangeOrder, setNewChangeOrder] = useState({
    number: `CO-00${mockChangeOrders.length + 1}`,
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'pending'
  });
  // Contract state
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [newContract, setNewContract] = useState({
    number: `CT-00${mockContracts.length + 1}`,
    title: '',
    vendor: '',
    amount: '',
    startDate: new Date().toISOString().split('T')[0],
    completionDate: '',
    status: 'pending',
    dateCreated: new Date().toISOString().split('T')[0],
    documents: [],
    terms: ''
  });
  const [showContractDetails, setShowContractDetails] = useState(false);
  const [contractFilter, setContractFilter] = useState('all');
  if (!project) {
    return <div className="text-center py-12" data-id="element-1083">
        <p className="text-gray-500" data-id="element-1084">Project not found.</p>
        <Link to="/projects" className="text-blue-600 hover:text-blue-800 mt-4 inline-block" data-id="element-1085">
          Back to Projects
        </Link>
      </div>;
  }
  const property = mockProperties.find(p => p.id === project.propertyId);
  // Calculate progress percentage
  const progressPercentage = Math.round(project.completedTasks / project.totalTasks * 100);
  // Define status color based on project status
  const getStatusColor = status => {
    switch (status) {
      case 'Planning':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-green-100 text-green-800';
      case 'At Risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'On Hold':
        return 'bg-gray-100 text-gray-800';
      case 'Completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getPhaseStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'not-started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getMaterialStatusColor = status => {
    switch (status) {
      case 'selected':
        return 'bg-blue-100 text-blue-800';
      case 'ordered':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'installed':
        return 'bg-green-800 text-white';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getChangeOrderStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getContractStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const handleNewChangeOrderSubmit = e => {
    e.preventDefault();
    // In a real app, we would send this to the backend
    console.log('Submitted change order:', newChangeOrder);
    setShowNewChangeOrderForm(false);
    // Reset form
    setNewChangeOrder({
      number: `CO-00${mockChangeOrders.length + 2}`,
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      status: 'pending'
    });
  };
  // Contract handlers
  const handleOpenContractModal = (contract = null) => {
    if (contract) {
      setSelectedContract(contract);
      setNewContract({
        ...contract
      });
    } else {
      setSelectedContract(null);
      setNewContract({
        number: `CT-00${mockContracts.length + 1}`,
        title: '',
        vendor: '',
        amount: '',
        startDate: new Date().toISOString().split('T')[0],
        completionDate: '',
        status: 'pending',
        dateCreated: new Date().toISOString().split('T')[0],
        documents: [],
        terms: ''
      });
    }
    setShowContractModal(true);
  };
  const handleContractSubmit = e => {
    e.preventDefault();
    // In a real app, we would send this to the backend
    console.log('Submitted contract:', newContract);
    setShowContractModal(false);
  };
  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      name: file.name,
      type: 'addendum',
      size: `${Math.round(file.size / 1024)} KB`
    }));
    setNewContract({
      ...newContract,
      documents: [...newContract.documents, ...newFiles]
    });
  };
  const removeDocument = index => {
    const updatedDocs = [...newContract.documents];
    updatedDocs.splice(index, 1);
    setNewContract({
      ...newContract,
      documents: updatedDocs
    });
  };
  const viewContractDetails = contract => {
    setSelectedContract(contract);
    setShowContractDetails(true);
  };
  const filteredContracts = contractFilter === 'all' ? mockContracts : mockContracts.filter(contract => contract.status === contractFilter);
  return <div data-id="element-1086">
      {/* Header with navigation */}
      <div className="mb-6" data-id="element-1087">
        <Link to="/projects" className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1088">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1089" />
          Back to Projects
        </Link>
      </div>
      {/* Project Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-1090">
        <div data-id="element-1091">
          <div className="flex items-center" data-id="element-1092">
            <h1 className="text-2xl font-bold text-gray-900 mr-3" data-id="element-1093">
              {project.name}
            </h1>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`} data-id="element-1094">
              {project.status}
            </span>
          </div>
          {property && <Link to={`/properties/${property.id}`} className="flex items-center text-blue-600 hover:text-blue-800 mt-1" data-id="element-1095">
              <BuildingIcon className="w-4 h-4 mr-1" data-id="element-1096" />
              <span data-id="element-1097">{property.name}</span>
            </Link>}
        </div>
        <div className="flex gap-2 mt-4 md:mt-0" data-id="element-1098">
          <Button variant="outline" data-id="element-1099">
            <EditIcon className="w-4 h-4 mr-2" data-id="element-1100" />
            Edit
          </Button>
          <Button variant="danger" data-id="element-1101">
            <TrashIcon className="w-4 h-4 mr-2" data-id="element-1102" />
            Delete
          </Button>
        </div>
      </div>
      {/* Project Quick Links */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6" data-id="element-1103">
        <div className="flex flex-wrap gap-3" data-id="element-1104">
          <Link to="/notes" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1105">
            <PencilIcon className="w-4 h-4 mr-2" data-id="element-1106" />
            <span data-id="element-1107">Daily Logs</span>
          </Link>
          <Link to="/todos" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1108">
            <ListTodoIcon className="w-4 h-4 mr-2" data-id="element-1109" />
            <span data-id="element-1110">To-Do List</span>
          </Link>
          <Link to="/file-cabinet" className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200" data-id="element-1111">
            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-1112" />
            <span data-id="element-1113">Documents</span>
          </Link>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6" data-id="element-1114">
        <nav className="flex -mb-px space-x-8 overflow-x-auto" data-id="element-1115">
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'overview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-1116">
            Overview
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'schedule' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('schedule')} data-id="element-1117">
            Schedule
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'budget' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('budget')} data-id="element-1118">
            Budget
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'contracts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('contracts')} data-id="element-1119">
            Contracts
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'materials' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('materials')} data-id="element-1120">
            Materials
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'logs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('logs')} data-id="element-1121">
            Logs & Tasks
          </button>
        </nav>
      </div>
      {/* Project Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-1122">
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="p-6" data-id="element-1123">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1124">
              <div className="lg:col-span-2" data-id="element-1125">
                <h3 className="text-lg font-medium mb-4" data-id="element-1126">Description</h3>
                <p className="text-gray-700 mb-6" data-id="element-1127">{project.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1128">
                  <div data-id="element-1129">
                    <h3 className="text-lg font-medium mb-4" data-id="element-1130">Timeline</h3>
                    <div className="space-y-3" data-id="element-1131">
                      <div className="flex justify-between" data-id="element-1132">
                        <div className="flex items-center text-gray-700" data-id="element-1133">
                          <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1134" />
                          <span data-id="element-1135">Start Date:</span>
                        </div>
                        <span className="font-medium" data-id="element-1136">{project.startDate}</span>
                      </div>
                      <div className="flex justify-between" data-id="element-1137">
                        <div className="flex items-center text-gray-700" data-id="element-1138">
                          <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1139" />
                          <span data-id="element-1140">End Date:</span>
                        </div>
                        <span className="font-medium" data-id="element-1141">{project.endDate}</span>
                      </div>
                      <div className="flex justify-between" data-id="element-1142">
                        <div className="flex items-center text-gray-700" data-id="element-1143">
                          <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1144" />
                          <span data-id="element-1145">Duration:</span>
                        </div>
                        <span className="font-medium" data-id="element-1146">
                          {project.duration} days
                        </span>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-1147">
                    <h3 className="text-lg font-medium mb-4" data-id="element-1148">Budget</h3>
                    <div className="space-y-3" data-id="element-1149">
                      <div className="flex justify-between" data-id="element-1150">
                        <span className="text-gray-700" data-id="element-1151">Total Budget:</span>
                        <span className="font-medium" data-id="element-1152">
                          ${project.budget.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1153">
                        <span className="text-gray-700" data-id="element-1154">Spent:</span>
                        <span className="font-medium" data-id="element-1155">
                          ${project.spent.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1156">
                        <span className="text-gray-700" data-id="element-1157">Remaining:</span>
                        <span className="font-medium" data-id="element-1158">
                          ${(project.budget - project.spent).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-medium mb-4" data-id="element-1159">Progress</h3>
                <div className="mb-2 flex justify-between" data-id="element-1160">
                  <span className="text-gray-700" data-id="element-1161">
                    Tasks Completed: {project.completedTasks}/
                    {project.totalTasks}
                  </span>
                  <span className="font-medium" data-id="element-1162">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1163">
                  <div className={`h-2.5 rounded-full ${project.status === 'At Risk' ? 'bg-yellow-500' : 'bg-blue-600'}`} style={{
                width: `${progressPercentage}%`
              }} data-id="element-1164"></div>
                </div>
              </div>
              <div data-id="element-1165">
                <h3 className="text-lg font-medium mb-4" data-id="element-1166">Key Tasks</h3>
                <div className="space-y-3" data-id="element-1167">
                  {project.tasks.map((task, index) => <div key={index} className="flex items-start" data-id="element-1168">
                      {task.completed ? <div className="mt-0.5 p-1 rounded-full bg-green-100" data-id="element-1169">
                          <CheckIcon className="w-4 h-4 text-green-600" data-id="element-1170" />
                        </div> : <div className="mt-0.5 p-1 rounded-full bg-gray-100" data-id="element-1171">
                          <XIcon className="w-4 h-4 text-gray-400" data-id="element-1172" />
                        </div>}
                      <div className="ml-3" data-id="element-1173">
                        <p className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`} data-id="element-1174">
                          {task.name}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-1175">{task.dueDate}</p>
                      </div>
                    </div>)}
                </div>
                <div className="mt-6" data-id="element-1176">
                  <h3 className="text-lg font-medium mb-4" data-id="element-1177">Team</h3>
                  <div className="space-y-3" data-id="element-1178">
                    {project.team.map((member, index) => <div key={index} className="flex items-center" data-id="element-1179">
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium" data-id="element-1180">
                          {member.name.charAt(0)}
                        </div>
                        <div className="ml-3" data-id="element-1181">
                          <p className="font-medium text-gray-900" data-id="element-1182">
                            {member.name}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-1183">{member.role}</p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Schedule Tab */}
        {activeTab === 'schedule' && <div className="p-6" data-id="element-1184">
            <div className="flex justify-between items-center mb-6" data-id="element-1185">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1186">
                Project Schedule
              </h3>
              <Button size="sm" data-id="element-1187">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-1188" />
                Add Phase
              </Button>
            </div>
            <div className="space-y-6" data-id="element-1189">
              {mockSchedule.map(phase => <div key={phase.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1190">
                  <div className="bg-gray-50 p-4 flex justify-between items-center" data-id="element-1191">
                    <div className="flex items-center" data-id="element-1192">
                      <h4 className="font-medium text-gray-900" data-id="element-1193">
                        {phase.name}
                      </h4>
                      <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(phase.status)}`} data-id="element-1194">
                        {phase.status.charAt(0).toUpperCase() + phase.status.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1195">
                      {phase.startDate} to {phase.endDate}
                    </div>
                  </div>
                  <div className="p-4" data-id="element-1196">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1197">
                      <thead data-id="element-1198">
                        <tr data-id="element-1199">
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1200">
                            Task
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1201">
                            Start Date
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1202">
                            End Date
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1203">
                            Status
                          </th>
                          <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1204">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1205">
                        {phase.tasks.map((task, index) => <tr key={index} data-id="element-1206">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1207">
                              {task.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1208">
                              {task.startDate}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1209">
                              {task.endDate}
                            </td>
                            <td className="px-4 py-3" data-id="element-1210">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(task.status)}`} data-id="element-1211">
                                {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1212">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1213">
                                Edit
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    <div className="mt-3 flex justify-end" data-id="element-1214">
                      <Button size="sm" variant="outline" data-id="element-1215">
                        <PlusIcon className="w-3 h-3 mr-1" data-id="element-1216" />
                        Add Task
                      </Button>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="mt-8" data-id="element-1217">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1218">
                Schedule Overview
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1219">
                <div className="relative" data-id="element-1220">
                  {/* Timeline header - months */}
                  <div className="flex border-b border-gray-300 pb-2" data-id="element-1221">
                    <div className="w-1/6" data-id="element-1222"></div>
                    {['Mar', 'Apr', 'May', 'Jun', 'Jul'].map((month, index) => <div key={index} className="flex-1 text-center text-xs font-medium text-gray-500" data-id="element-1223">
                        {month} 2023
                      </div>)}
                  </div>
                  {/* Timeline for each phase */}
                  {mockSchedule.map((phase, phaseIndex) => <div key={phase.id} className={`flex items-center py-3 ${phaseIndex !== mockSchedule.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-1224">
                      <div className="w-1/6 pr-4" data-id="element-1225">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1226">
                          {phase.name}
                        </p>
                      </div>
                      <div className="flex-1 relative h-6" data-id="element-1227">
                        {/* Calculate position and width based on dates */}
                        <div className={`absolute h-full rounded-full ${phase.status === 'completed' ? 'bg-green-500' : phase.status === 'in-progress' ? 'bg-blue-500' : 'bg-gray-300'}`} style={{
                    left: '0%',
                    width: phaseIndex === 0 ? '20%' : phaseIndex === 1 ? '15%' : phaseIndex === 2 ? '25%' : phaseIndex === 3 ? '30%' : '15%'
                  }} data-id="element-1228"></div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>}
        {/* Budget Tab */}
        {activeTab === 'budget' && <div className="p-6" data-id="element-1229">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-1230">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1231">
                Project Budget
              </h3>
              <div className="mt-3 md:mt-0" data-id="element-1232">
                <Button size="sm" data-id="element-1233">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1234" />
                  Add Budget Item
                </Button>
              </div>
            </div>
            {/* Budget Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-1235">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1236">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1237">Original Budget</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1238">
                  ${mockBudget.summary.originalTotal.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1239">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1240">Current Budget</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1241">
                  ${mockBudget.summary.currentTotal.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1242">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1243">Actual to Date</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1244">
                  ${mockBudget.summary.actualToDate.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1245">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1246">Remaining</p>
                <p className="text-xl font-semibold text-gray-900" data-id="element-1247">
                  ${mockBudget.summary.remainingBudget.toLocaleString()}
                </p>
              </div>
            </div>
            {/* Budget Categories */}
            <div className="mb-8" data-id="element-1248">
              <h4 className="text-md font-medium text-gray-900 mb-3" data-id="element-1249">
                Budget Categories
              </h4>
              <div className="overflow-x-auto" data-id="element-1250">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1251">
                  <thead data-id="element-1252">
                    <tr data-id="element-1253">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1254">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1255">
                        Original Budget
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1256">
                        Current Budget
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1257">
                        Actual Cost
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1258">
                        Variance
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1259">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1260">
                    {mockBudget.categories.map((category, index) => <tr key={index} data-id="element-1261">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1262">
                          {category.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1263">
                          ${category.original.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1264">
                          ${category.current.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1265">
                          ${category.actual.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium text-right ${category.variance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1266">
                          {category.variance >= 0 ? '+' : ''}
                          {category.variance.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1267">
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-1268">
                            Edit
                          </button>
                        </td>
                      </tr>)}
                    <tr className="bg-gray-50" data-id="element-1269">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1270">
                        Total
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1271">
                        ${mockBudget.summary.originalTotal.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1272">
                        ${mockBudget.summary.currentTotal.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1273">
                        ${mockBudget.summary.actualToDate.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium text-right ${mockBudget.summary.overallVariance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1274">
                        {mockBudget.summary.overallVariance >= 0 ? '+' : ''}
                        {mockBudget.summary.overallVariance.toLocaleString()}
                      </td>
                      <td className="px-4 py-3" data-id="element-1275"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {/* Change Orders */}
            <div data-id="element-1276">
              <div className="flex justify-between items-center mb-4" data-id="element-1277">
                <h4 className="text-md font-medium text-gray-900" data-id="element-1278">
                  Change Orders
                </h4>
                <Button size="sm" onClick={() => setShowNewChangeOrderForm(!showNewChangeOrderForm)} data-id="element-1279">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1280" />
                  New Change Order
                </Button>
              </div>
              {/* New Change Order Form */}
              {showNewChangeOrderForm && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" data-id="element-1281">
                  <h5 className="font-medium text-gray-900 mb-3" data-id="element-1282">
                    New Change Order
                  </h5>
                  <form onSubmit={handleNewChangeOrderSubmit} data-id="element-1283">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1284">
                      <div data-id="element-1285">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1286">
                          Change Order #
                        </label>
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.number} readOnly data-id="element-1287" />
                      </div>
                      <div data-id="element-1288">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1289">
                          Date
                        </label>
                        <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.date} onChange={e => setNewChangeOrder({
                    ...newChangeOrder,
                    date: e.target.value
                  })} data-id="element-1290" />
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-1291">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1292">
                        Description
                      </label>
                      <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" rows="3" value={newChangeOrder.description} onChange={e => setNewChangeOrder({
                  ...newChangeOrder,
                  description: e.target.value
                })} placeholder="Describe the change order..." data-id="element-1293"></textarea>
                    </div>
                    <div className="mb-4" data-id="element-1294">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1295">
                        Amount ($)
                      </label>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newChangeOrder.amount} onChange={e => setNewChangeOrder({
                  ...newChangeOrder,
                  amount: e.target.value
                })} placeholder="0.00" data-id="element-1296" />
                    </div>
                    <div className="flex justify-end space-x-3" data-id="element-1297">
                      <Button variant="outline" type="button" onClick={() => setShowNewChangeOrderForm(false)} data-id="element-1298">
                        Cancel
                      </Button>
                      <Button type="submit" data-id="element-1299">Submit Change Order</Button>
                    </div>
                  </form>
                </div>}
              <div className="overflow-x-auto" data-id="element-1300">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1301">
                  <thead data-id="element-1302">
                    <tr data-id="element-1303">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1304">
                        CO #
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1305">
                        Date
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1306">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1307">
                        Amount
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1308">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1309">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1310">
                    {mockChangeOrders.map(co => <tr key={co.id} data-id="element-1311">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1312">
                          {co.number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1313">
                          {co.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1314">
                          {co.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1315">
                          ${co.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1316">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChangeOrderStatusColor(co.status)}`} data-id="element-1317">
                            {co.status.charAt(0).toUpperCase() + co.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1318">
                          <button className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-1319">
                            Edit
                          </button>
                          <button className="text-blue-600 hover:text-blue-900" data-id="element-1320">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
        {/* Contracts Tab */}
        {activeTab === 'contracts' && <div className="p-6" data-id="element-1321">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6" data-id="element-1322">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1323">
                Project Contracts
              </h3>
              <div className="mt-3 md:mt-0 flex items-center gap-4" data-id="element-1324">
                <div className="flex items-center" data-id="element-1325">
                  <label htmlFor="contractFilter" className="mr-2 text-sm text-gray-600" data-id="element-1326">
                    Filter:
                  </label>
                  <select id="contractFilter" className="border border-gray-300 rounded-md text-sm p-1.5" value={contractFilter} onChange={e => setContractFilter(e.target.value)} data-id="element-1327">
                    <option value="all" data-id="element-1328">All Contracts</option>
                    <option value="pending" data-id="element-1329">Pending</option>
                    <option value="in-progress" data-id="element-1330">In Progress</option>
                    <option value="completed" data-id="element-1331">Completed</option>
                    <option value="terminated" data-id="element-1332">Terminated</option>
                  </select>
                </div>
                <Button size="sm" onClick={() => handleOpenContractModal()} data-id="element-1333">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1334" />
                  New Contract
                </Button>
              </div>
            </div>
            {/* Contracts List */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1335">
              <div className="overflow-x-auto" data-id="element-1336">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1337">
                  <thead className="bg-gray-50" data-id="element-1338">
                    <tr data-id="element-1339">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1340">
                        Contract #
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1341">
                        Title
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1342">
                        Vendor
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1343">
                        Dates
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1344">
                        Amount
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1345">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1346">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1347">
                    {filteredContracts.map(contract => <tr key={contract.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => viewContractDetails(contract)} data-id="element-1348">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1349">
                          {contract.number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1350">
                          {contract.title}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1351">
                          {contract.vendor}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1352">
                          <div data-id="element-1353">{contract.startDate}</div>
                          <div className="text-xs text-gray-400" data-id="element-1354">
                            to {contract.completionDate}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1355">
                          ${contract.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1356">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContractStatusColor(contract.status)}`} data-id="element-1357">
                            {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1358">
                          <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={e => {
                      e.stopPropagation();
                      handleOpenContractModal(contract);
                    }} data-id="element-1359">
                            Edit
                          </button>
                          <button className="text-blue-600 hover:text-blue-900" onClick={e => {
                      e.stopPropagation();
                      // Download function would go here
                      console.log('Download contract', contract.id);
                    }} data-id="element-1360">
                            <DownloadIcon className="w-4 h-4" data-id="element-1361" />
                          </button>
                        </td>
                      </tr>)}
                    {filteredContracts.length === 0 && <tr data-id="element-1362">
                        <td colSpan="7" className="px-4 py-6 text-center text-gray-500" data-id="element-1363">
                          No contracts found. Click "New Contract" to create
                          one.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
        {/* Materials Tab */}
        {activeTab === 'materials' && <div className="p-6" data-id="element-1364">
            <div className="flex justify-between items-center mb-6" data-id="element-1365">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1366">
                Material Selections
              </h3>
              <div data-id="element-1367">
                <Button size="sm" data-id="element-1368">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1369" />
                  Add Material
                </Button>
              </div>
            </div>
            {/* Material Categories */}
            <div className="space-y-8" data-id="element-1370">
              {mockMaterials.categories.map(category => <div key={category.name} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1371">
                  <div className="bg-gray-50 p-4" data-id="element-1372">
                    <h4 className="font-medium text-gray-900" data-id="element-1373">
                      {category.name}
                    </h4>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1374">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1375">
                      <thead data-id="element-1376">
                        <tr data-id="element-1377">
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1378">
                            Item
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1379">
                            Description
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1380">
                            Brand/Model
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1381">
                            Unit Cost
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1382">
                            Quantity
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1383">
                            Total
                          </th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1384">
                            Status
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1385">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1386">
                        {category.items.map((item, index) => <tr key={index} data-id="element-1387">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1388">
                              {item.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1389">
                              {item.description}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1390">
                              {item.brand} {item.model}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1391">
                              ${item.cost.toFixed(2)}/{item.unit}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1392">
                              {item.quantity} {item.unit}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1393">
                              ${item.totalCost.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1394">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMaterialStatusColor(item.status)}`} data-id="element-1395">
                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium" data-id="element-1396">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1397">
                                Edit
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>)}
            </div>
            {/* Specifications */}
            <div className="mt-8" data-id="element-1398">
              <div className="flex justify-between items-center mb-4" data-id="element-1399">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1400">
                  Specifications
                </h3>
                <Button size="sm" data-id="element-1401">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-1402" />
                  Add Specification
                </Button>
              </div>
              <div className="space-y-4" data-id="element-1403">
                {mockSpecifications.map(spec => <div key={spec.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50" data-id="element-1404">
                    <div className="flex justify-between items-start" data-id="element-1405">
                      <div data-id="element-1406">
                        <h4 className="font-medium text-gray-900" data-id="element-1407">
                          {spec.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-1408">
                          {spec.content}
                        </p>
                      </div>
                      <div className="flex items-center" data-id="element-1409">
                        <span className="text-xs text-gray-500 mr-3" data-id="element-1410">
                          Last updated: {spec.lastUpdated}
                        </span>
                        <button className="text-blue-600 hover:text-blue-900 text-sm font-medium" data-id="element-1411">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div className="mt-2" data-id="element-1412">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1413">
                        {spec.category}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
        {/* Logs & Tasks Tab */}
        {activeTab === 'logs' && <div className="p-6" data-id="element-1414">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1415">
              {/* Daily Logs Section */}
              <div data-id="element-1416">
                <div className="flex justify-between items-center mb-4" data-id="element-1417">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1418">
                    Daily Logs
                  </h3>
                  <Link to="/notes" className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" data-id="element-1419">
                    View All Logs
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-1420" />
                  </Link>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-1421">
                  <div className="border-b border-gray-200" data-id="element-1422">
                    <div className="p-4 flex items-center justify-between" data-id="element-1423">
                      <div className="flex items-center" data-id="element-1424">
                        <div className="p-1.5 rounded-full bg-blue-100" data-id="element-1425">
                          <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-1426" />
                        </div>
                        <div className="ml-3" data-id="element-1427">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-1428">
                            Site Progress Update
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-1429">
                            Today at 10:30 AM
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1430">
                        On Schedule
                      </span>
                    </div>
                    <div className="px-4 pb-4" data-id="element-1431">
                      <p className="text-sm text-gray-600" data-id="element-1432">
                        HVAC installation in progress. 70% complete. All
                        materials on site and work proceeding as planned.
                      </p>
                    </div>
                  </div>
                  <div className="border-b border-gray-200" data-id="element-1433">
                    <div className="p-4 flex items-center justify-between" data-id="element-1434">
                      <div className="flex items-center" data-id="element-1435">
                        <div className="p-1.5 rounded-full bg-yellow-100" data-id="element-1436">
                          <AlertCircleIcon className="w-4 h-4 text-yellow-600" data-id="element-1437" />
                        </div>
                        <div className="ml-3" data-id="element-1438">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-1439">
                            Inspection Notice
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-1440">
                            Yesterday at 4:15 PM
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1441">
                        Attention Needed
                      </span>
                    </div>
                    <div className="px-4 pb-4" data-id="element-1442">
                      <p className="text-sm text-gray-600" data-id="element-1443">
                        Rough-in plumbing inspection scheduled for tomorrow at
                        9:00 AM. Need to ensure all work is ready for review.
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1444">
                    <div className="p-4 flex items-center justify-between" data-id="element-1445">
                      <div className="flex items-center" data-id="element-1446">
                        <div className="p-1.5 rounded-full bg-purple-100" data-id="element-1447">
                          <FileTextIcon className="w-4 h-4 text-purple-600" data-id="element-1448" />
                        </div>
                        <div className="ml-3" data-id="element-1449">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-1450">
                            Material Delivery
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-1451">
                            May 3, 2023 at 1:45 PM
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-4" data-id="element-1452">
                      <p className="text-sm text-gray-600" data-id="element-1453">
                        Electrical supplies delivered and inventoried. All items
                        accounted for and stored securely on site.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center" data-id="element-1454">
                  <Link to="/notes" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1455">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-1456" />
                    Create New Log Entry
                  </Link>
                </div>
              </div>
              {/* Tasks Section */}
              <div data-id="element-1457">
                <div className="flex justify-between items-center mb-4" data-id="element-1458">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1459">
                    Project Tasks
                  </h3>
                  <Link to="/todos" className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" data-id="element-1460">
                    View All Tasks
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-1461" />
                  </Link>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200" data-id="element-1462">
                  <div className="p-4 flex items-start" data-id="element-1463">
                    <div className="mr-3 mt-1" data-id="element-1464">
                      <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center" data-id="element-1465">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-1466"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-1467">
                      <div className="flex justify-between" data-id="element-1468">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-1469">
                          Schedule final HVAC inspection
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1470">
                          High Priority
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1471">
                        Due: May 20, 2023 • Assigned to: Sarah Johnson
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex items-start" data-id="element-1472">
                    <div className="mr-3 mt-1" data-id="element-1473">
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1474"></div>
                    </div>
                    <div className="flex-1" data-id="element-1475">
                      <div className="flex justify-between" data-id="element-1476">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-1477">
                          Order remaining bathroom fixtures
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1478">
                          Medium Priority
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1479">
                        Due: May 25, 2023 • Assigned to: Maria Garcia
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex items-start" data-id="element-1480">
                    <div className="mr-3 mt-1" data-id="element-1481">
                      <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1482">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-1483" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-1484">
                      <div className="flex justify-between" data-id="element-1485">
                        <h4 className="text-sm font-medium text-gray-500 line-through" data-id="element-1486">
                          Review electrical plans with contractor
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1487">
                          Completed
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1488">
                        Completed: May 2, 2023 • Assigned to: John Smith
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center" data-id="element-1489">
                  <Link to="/todos" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1490">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-1491" />
                    Create New Task
                  </Link>
                </div>
              </div>
            </div>
          </div>}
      </div>
      {/* Contract Modal */}
      {showContractModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1492">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1493">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1494">
              {selectedContract ? 'Edit Contract' : 'New Contract'}
            </h3>
            <form onSubmit={handleContractSubmit} data-id="element-1495">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1496">
                <div data-id="element-1497">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1498">
                    Contract #
                  </label>
                  <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.number} readOnly data-id="element-1499" />
                </div>
                <div data-id="element-1500">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1501">
                    Date Created
                  </label>
                  <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.dateCreated} readOnly data-id="element-1502" />
                </div>
              </div>
              <div className="mb-4" data-id="element-1503">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1504">
                  Contract Title
                </label>
                <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.title} onChange={e => setNewContract({
              ...newContract,
              title: e.target.value
            })} placeholder="Enter contract title..." required data-id="element-1505" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1506">
                <div data-id="element-1507">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1508">
                    Vendor Name
                  </label>
                  <div className="relative" data-id="element-1509">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1510">
                      <UserIcon className="h-4 w-4 text-gray-400" data-id="element-1511" />
                    </div>
                    <input type="text" className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.vendor} onChange={e => setNewContract({
                  ...newContract,
                  vendor: e.target.value
                })} placeholder="Enter vendor name..." required data-id="element-1512" />
                  </div>
                </div>
                <div data-id="element-1513">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1514">
                    Contract Amount
                  </label>
                  <div className="relative" data-id="element-1515">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1516">
                      <span className="text-gray-500 sm:text-sm" data-id="element-1517">$</span>
                    </div>
                    <input type="text" className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.amount} onChange={e => setNewContract({
                  ...newContract,
                  amount: e.target.value.replace(/[^0-9]/g, '')
                })} placeholder="0.00" required data-id="element-1518" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1519">
                <div data-id="element-1520">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1521">
                    Start Date
                  </label>
                  <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.startDate} onChange={e => setNewContract({
                ...newContract,
                startDate: e.target.value
              })} required data-id="element-1522" />
                </div>
                <div data-id="element-1523">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1524">
                    Completion Date
                  </label>
                  <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.completionDate} onChange={e => setNewContract({
                ...newContract,
                completionDate: e.target.value
              })} required data-id="element-1525" />
                </div>
              </div>
              <div className="mb-4" data-id="element-1526">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1527">
                  Status
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newContract.status} onChange={e => setNewContract({
              ...newContract,
              status: e.target.value
            })} required data-id="element-1528">
                  <option value="pending" data-id="element-1529">Pending</option>
                  <option value="in-progress" data-id="element-1530">In Progress</option>
                  <option value="completed" data-id="element-1531">Completed</option>
                  <option value="terminated" data-id="element-1532">Terminated</option>
                </select>
              </div>
              <div className="mb-4" data-id="element-1533">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1534">
                  Terms & Conditions
                </label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" rows="4" value={newContract.terms} onChange={e => setNewContract({
              ...newContract,
              terms: e.target.value
            })} placeholder="Enter contract terms and conditions..." data-id="element-1535"></textarea>
              </div>
              <div className="mb-4" data-id="element-1536">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1537">
                  Documents
                </label>
                {/* Document list */}
                <div className="border border-gray-200 rounded-md mb-3" data-id="element-1538">
                  {newContract.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-1539">
                      {newContract.documents.map((doc, index) => <li key={index} className="p-3 flex items-center justify-between" data-id="element-1540">
                          <div className="flex items-center" data-id="element-1541">
                            <FileIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1542" />
                            <div data-id="element-1543">
                              <p className="text-sm font-medium text-gray-700" data-id="element-1544">
                                {doc.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1545">
                                {doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}{' '}
                                • {doc.size}
                              </p>
                            </div>
                          </div>
                          <button type="button" className="text-red-600 hover:text-red-800 text-sm" onClick={() => removeDocument(index)} data-id="element-1546">
                            Remove
                          </button>
                        </li>)}
                    </ul> : <div className="p-4 text-center text-gray-500 text-sm" data-id="element-1547">
                      No documents attached. Upload documents below.
                    </div>}
                </div>
                {/* Upload button */}
                <div className="flex items-center justify-center" data-id="element-1548">
                  <label className="cursor-pointer bg-white border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-1549">
                    <span className="flex items-center" data-id="element-1550">
                      <UploadIcon className="h-4 w-4 mr-2" data-id="element-1551" />
                      Upload Document
                    </span>
                    <input type="file" className="hidden" multiple onChange={handleFileUpload} data-id="element-1552" />
                  </label>
                </div>
              </div>
              {/* Project details that will be auto-populated */}
              <div className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-1553">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1554">
                  Project Details (Auto-populated)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-id="element-1555">
                  <div data-id="element-1556">
                    <p className="text-gray-500" data-id="element-1557">Project Name:</p>
                    <p className="font-medium" data-id="element-1558">{project.name}</p>
                  </div>
                  <div data-id="element-1559">
                    <p className="text-gray-500" data-id="element-1560">Property Address:</p>
                    <p className="font-medium" data-id="element-1561">{property?.address || 'N/A'}</p>
                  </div>
                  <div data-id="element-1562">
                    <p className="text-gray-500" data-id="element-1563">Project Start Date:</p>
                    <p className="font-medium" data-id="element-1564">{project.startDate}</p>
                  </div>
                  <div data-id="element-1565">
                    <p className="text-gray-500" data-id="element-1566">Project End Date:</p>
                    <p className="font-medium" data-id="element-1567">{project.endDate}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-1568">
                <Button variant="outline" type="button" onClick={() => setShowContractModal(false)} data-id="element-1569">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-1570">
                  {selectedContract ? 'Update Contract' : 'Create Contract'}
                </Button>
              </div>
            </form>
          </div>
        </div>}
      {/* Contract Details Modal */}
      {showContractDetails && selectedContract && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1571">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1572">
            <div className="flex justify-between items-start mb-4" data-id="element-1573">
              <div data-id="element-1574">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1575">
                  {selectedContract.title}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-1576">
                  Contract #{selectedContract.number} • Created on{' '}
                  {selectedContract.dateCreated}
                </p>
              </div>
              <button className="text-gray-400 hover:text-gray-500" onClick={() => setShowContractDetails(false)} data-id="element-1577">
                <XIcon className="h-5 w-5" data-id="element-1578" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1579">
              <div data-id="element-1580">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1581">
                  Contract Details
                </h4>
                <div className="space-y-3" data-id="element-1582">
                  <div data-id="element-1583">
                    <p className="text-xs text-gray-500" data-id="element-1584">Vendor</p>
                    <p className="font-medium" data-id="element-1585">{selectedContract.vendor}</p>
                  </div>
                  <div data-id="element-1586">
                    <p className="text-xs text-gray-500" data-id="element-1587">Contract Amount</p>
                    <p className="font-medium" data-id="element-1588">
                      ${selectedContract.amount.toLocaleString()}
                    </p>
                  </div>
                  <div data-id="element-1589">
                    <p className="text-xs text-gray-500" data-id="element-1590">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContractStatusColor(selectedContract.status)}`} data-id="element-1591">
                      {selectedContract.status.charAt(0).toUpperCase() + selectedContract.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-1592">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1593">
                  Timeline
                </h4>
                <div className="space-y-3" data-id="element-1594">
                  <div data-id="element-1595">
                    <p className="text-xs text-gray-500" data-id="element-1596">Start Date</p>
                    <p className="font-medium" data-id="element-1597">{selectedContract.startDate}</p>
                  </div>
                  <div data-id="element-1598">
                    <p className="text-xs text-gray-500" data-id="element-1599">Completion Date</p>
                    <p className="font-medium" data-id="element-1600">
                      {selectedContract.completionDate}
                    </p>
                  </div>
                  <div data-id="element-1601">
                    <p className="text-xs text-gray-500" data-id="element-1602">Duration</p>
                    <p className="font-medium" data-id="element-1603">
                      {Math.ceil((new Date(selectedContract.completionDate) - new Date(selectedContract.startDate)) / (1000 * 60 * 60 * 24))}{' '}
                      days
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1604">
              <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1605">
                Terms & Conditions
              </h4>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 text-sm text-gray-700" data-id="element-1606">
                {selectedContract.terms || 'No terms specified.'}
              </div>
            </div>
            <div className="mb-6" data-id="element-1607">
              <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1608">
                Documents
              </h4>
              {selectedContract.documents.length > 0 ? <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200" data-id="element-1609">
                  {selectedContract.documents.map((doc, index) => <div key={index} className="p-3 flex items-center justify-between" data-id="element-1610">
                      <div className="flex items-center" data-id="element-1611">
                        <FileIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1612" />
                        <div data-id="element-1613">
                          <p className="text-sm font-medium text-gray-700" data-id="element-1614">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1615">
                            {doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}{' '}
                            • {doc.size}
                          </p>
                        </div>
                      </div>
                      <button type="button" className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-1616">
                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1617" />
                        Download
                      </button>
                    </div>)}
                </div> : <div className="bg-gray-50 p-4 rounded-md border border-gray-200 text-center text-gray-500 text-sm" data-id="element-1618">
                  No documents attached to this contract.
                </div>}
            </div>
            <div className="flex justify-between" data-id="element-1619">
              <Button variant="outline" type="button" onClick={() => {
            setShowContractDetails(false);
            handleOpenContractModal(selectedContract);
          }} data-id="element-1620">
                <EditIcon className="w-4 h-4 mr-2" data-id="element-1621" />
                Edit Contract
              </Button>
              <div className="space-x-3" data-id="element-1622">
                <Button variant="outline" type="button" onClick={() => setShowContractDetails(false)} data-id="element-1623">
                  Close
                </Button>
                <Button type="button" onClick={() => {
              // Download contract function would go here
              console.log('Download contract', selectedContract.id);
            }} data-id="element-1624">
                  <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1625" />
                  Download Contract
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default ProjectDetail;